import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, ChevronDown, Shield, Users, Scale } from 'lucide-react';
import { Button } from './ui/button';
import { GmedLogo } from './Logo';
import { useTheme } from './ThemeProvider';
import { menuSettingsAPI, pagesAPI } from '../services/api';

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isComplianceOpen, setIsComplianceOpen] = useState(false);
  const [menuSettings, setMenuSettings] = useState({
    home: true,
    about: true,
    products: true,
    portfolio: true,
    certifications: true,
    team: true,
    testimonials: true,
    blog: true,
    compliance: true,
    contact: true,
    custom_pages: {},
  });
  const [customPages, setCustomPages] = useState([]);

  // Map path to settings key
  const pathToKey = {
    '/': 'home',
    '/about': 'about',
    '/products': 'products',
    '/portfolio': 'portfolio',
    '/certifications': 'certifications',
    '/team': 'team',
    '/testimonials': 'testimonials',
    '/blog': 'blog',
  };

  const allNavItems = [
    { path: '/', label: t('nav.home'), key: 'home' },
    { path: '/about', label: t('nav.about'), key: 'about' },
    { path: '/products', label: t('nav.products'), key: 'products' },
    { path: '/portfolio', label: t('nav.portfolio'), key: 'portfolio' },
    { path: '/certifications', label: t('nav.certifications'), key: 'certifications' },
    { path: '/team', label: t('nav.team'), key: 'team' },
    { path: '/testimonials', label: t('nav.testimonials'), key: 'testimonials' },
    { path: '/blog', label: t('nav.blog'), key: 'blog' },
  ];

  // Filter nav items based on menu settings
  const navItems = allNavItems.filter(item => menuSettings[item.key]);

  // Custom pages enabled in menu settings
  const customNavItems = customPages
    .filter(page => menuSettings.custom_pages?.[page.slug])
    .map(page => ({ path: `/${page.slug}`, label: page.title, key: `custom-${page.slug}` }));

  const complianceItems = [
    { path: '/compliance/privacy', label: 'Politica Privacy', icon: Shield },
    { path: '/compliance/dpo', label: 'Responsabile Protezione Dati', icon: Users },
    { path: '/compliance/gender-equality', label: 'Parità di Genere', icon: Scale },
  ];

  useEffect(() => {
    const fetchMenuSettings = async () => {
      try {
        const [settingsRes, pagesRes] = await Promise.all([
          menuSettingsAPI.get(),
          pagesAPI.getAll().catch(() => ({ data: [] })),
        ]);
        setMenuSettings(prev => ({ ...prev, ...settingsRes.data }));
        setCustomPages(pagesRes.data);
      } catch (error) {
        console.error('Error loading menu settings:', error);
      }
    };
    fetchMenuSettings();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsComplianceOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'it' ? 'en' : 'it';
    i18n.changeLanguage(newLang);
  };

  const isCompliancePage = location.pathname.startsWith('/compliance');
  
  // White text only works over the dark hero: in light theme keep the solid navbar
  // so the links never end up white on white (WCAG 1.4.3)
  const solidNav = isScrolled || theme === 'light';
  const navTextClass = solidNav ? 'text-foreground' : 'text-white';
  const navTextActiveClass = solidNav ? 'text-primary font-semibold' : 'text-white font-semibold';
  const navTextHoverClass = solidNav ? 'hover:text-primary hover:bg-muted' : 'hover:text-white/80';

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solidNav
          ? 'glass shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <GmedLogo className="h-10 w-auto transition-transform group-hover:scale-105" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  location.pathname === item.path
                    ? navTextActiveClass
                    : `${navTextClass} ${navTextHoverClass}`
                }`}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Compliance Dropdown - only show if enabled */}
            {menuSettings.compliance && (
              <div className="relative" data-testid="compliance-dropdown">
                <button
                  onClick={() => setIsComplianceOpen(!isComplianceOpen)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 flex items-center space-x-1 ${
                    isCompliancePage
                      ? navTextActiveClass
                      : `${navTextClass} ${navTextHoverClass}`
                  }`}
                >
                  <span>Compliance</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isComplianceOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {isComplianceOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-0 mt-2 w-64 glass rounded-lg shadow-xl border border-border overflow-hidden"
                    >
                      {complianceItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center space-x-3 px-4 py-3 text-sm transition-colors ${
                            location.pathname === item.path
                              ? 'bg-primary/10 text-primary'
                              : 'text-foreground hover:bg-muted'
                          }`}
                          data-testid={`compliance-link-${item.path.split('/').pop()}`}
                        >
                          <item.icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Custom Pages - desktop */}
            {customNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  location.pathname === item.path
                    ? navTextActiveClass
                    : `${navTextClass} ${navTextHoverClass}`
                }`}
                data-testid={`nav-${item.key}`}
              >
                {item.label}
              </Link>
            ))}

            {/* Contact - only show if enabled */}
            {menuSettings.contact && (
              <Link
                to="/contact"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  location.pathname === '/contact'
                    ? navTextActiveClass
                    : `${navTextClass} ${navTextHoverClass}`
                }`}
              >
                {t('nav.contact')}
              </Link>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className={`rounded-full ${!solidNav ? 'text-white hover:text-white/80 hover:bg-white/10' : ''}`}
              data-testid="language-toggle"
            >
              <Globe className="h-5 w-5" />
              <span className="sr-only">Toggle language</span>
            </Button>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className={`lg:hidden rounded-full ${!solidNav ? 'text-white hover:text-white/80 hover:bg-white/10' : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-border"
          >
            <div className="container mx-auto px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all ${
                    location.pathname === item.path
                      ? 'bg-primary text-white'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Mobile Compliance - only show if enabled */}
              {menuSettings.compliance && (
                <div className="border-t border-border pt-2 mt-2">
                  <p className="px-4 py-2 text-sm font-semibold text-muted-foreground">Compliance</p>
                  {complianceItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all ${
                        location.pathname === item.path
                          ? 'bg-primary text-white'
                          : 'text-foreground hover:bg-muted'
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}

              {/* Custom Pages - mobile */}
              {customNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all ${
                    location.pathname === item.path
                      ? 'bg-primary text-white'
                      : 'text-foreground hover:bg-muted'
                  }`}
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {item.label}
                </Link>
              ))}

              {/* Mobile Contact - only show if enabled */}
              {menuSettings.contact && (
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all ${
                    location.pathname === '/contact'
                      ? 'bg-primary text-white'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {t('nav.contact')}
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
